import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Space, Modal, Button, Table, Typography, Form } from 'antd';
import { ExclamationCircleFilled, PlusOutlined } from '@ant-design/icons';
import _ from 'lodash';

import { setAppTitle } from 'src/store/app';

// import { APP_LANGUAGES } from 'src/constants'; // not used right now, can remove

import { dumpSelectedApp } from 'src/utils/dumps';

import './builder.scss';

const { confirm } = Modal;
const { Text } = Typography;

const ModalFooter = (props) => {
  const { onCancel, isLoading } = props;

  const { t } = useTranslation('builder');

  return (
    <div className="modal__footer">
      <Button style={{ width: '100%' }} onClick={onCancel} disabled={isLoading}>
        {t('cancel')}
      </Button>
      <Button
        style={{ width: '100%' }}
        type="primary"
        htmlType="submit"
        loading={isLoading}
        disabled={isLoading}
      >
        {t('submit')}
      </Button>
    </div>
  );
};

export const Builder = (props) => {
  const { build } = props;

  const { t } = useTranslation('builder');
  const dispatch = useDispatch();

  const {
    title,
    create_title,
    update_title,
    table,
    // filters,
    api,
    modals,
    loaders
  } = build;

  // const { filters_body } = filters;

  const { table_columns, table_data, table_meta } = table;
  const { modal_body } = modals;
  const { create, read, update, delete_item } = api;
  const { is_loading_create, is_loading_read, is_loading_update, is_loading_delete } = loaders;

  // 🔹 Pagination / search meta
  const [metaData, setMetaData] = useState({
    page: 1,
    perPage: 10,
    search: ''
  });

  // 🔹 Modal + edit mode state
  const [isModal, setIsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState({});

  // 🔹 JSON object with all current form values
  const [formValues, setFormValues] = useState({});

  const isEditMode = !_.isEmpty(selectedItem);

  const modalTitle = useMemo(
    () => (isEditMode ? update_title : create_title),
    [isEditMode, create_title, update_title]
  );

  // 🔹 Set page title
  useEffect(() => {
    dispatch(setAppTitle(title));
  }, [dispatch, title]);

  // 🔹 Fetch table data when filters / pagination change
  useEffect(() => {
    if (metaData.search) {
      const debounce = setTimeout(() => {
        dispatch(read(metaData));
      }, 500);

      return () => clearTimeout(debounce);
    }

    dispatch(read(metaData));
  }, [dispatch, read, metaData]);

  const onChangePagination = useCallback((page, pageSize) => {
    setMetaData((prev) => ({
      ...prev,
      page,
      perPage: pageSize
    }));
  }, []);

  const onClickDelete = useCallback(
    (item) => () => {
      confirm({
        title: `Are you sure what you want to delete - ${item.appName}?`,
        icon: <ExclamationCircleFilled />,
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'Cancel',
        maskClosable: true,
        centered: true,
        onOk: () => {
          dispatch(delete_item({ id: item._id }));
        },
        onCancel: () => false
      });
    },
    [dispatch, delete_item]
  );

  // 🔹 Edit mode: prefill formValues from selected row
  const onClickEdit = useCallback(
    (item) => () => {
      const data = dumpSelectedApp(item); // normalizes item

      setFormValues(data);
      setSelectedItem(item);
      setIsModal(true);
    },
    []
  );

  // 🔹 Create mode: clear values
  const onShowModal = useCallback(() => {
    setFormValues({});
    setSelectedItem({});
    setIsModal(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsModal(false);
    setSelectedItem({});
    setFormValues({});
  }, []);

  // 🔹 Update JSON values on any field change
  const onChangeForm = useCallback((changedValues, allValues) => {
    // changedValues: { field: value }
    // allValues: full object
    setFormValues(allValues);
  }, []);

  // 🔹 Submit handler uses values from AntD Form
  const onFinishForm = useCallback(
    async (values) => {
      if (!isEditMode) {
        await dispatch(create({ payload: values }));
      } else {
        await dispatch(update({ id: selectedItem._id, payload: values }));
      }

      setFormValues({});
      setSelectedItem({});
      setIsModal(false);
    },
    [dispatch, create, update, isEditMode, selectedItem]
  );

  // 🔹 Render fields from build.modals.modal_body(formValues)
  const renderModalBody = useCallback(() => {
    const body = typeof modal_body === 'function' ? modal_body(formValues) : modal_body;

    return body.flatMap((item) => {
      const Component = item.component;

      return (
        <Fragment key={item.id}>
          <Component
            name={item.id}
            formData={formValues} // now plain JSON object
            {...item.options}
            label={item.options.label}
          />
        </Fragment>
      );
    });
  }, [modal_body, formValues]);

  const updatedTableColumns = [
    ...table_columns,
    {
      title: 'Action',
      key: 'operation',
      fixed: 'right',
      // width: 150,
      render: (item) => (
        <Space size="small" key={item._id}>
          <Button type="primary" size="small" onClick={onClickEdit(item)}>
            {t('edit')}
          </Button>
          <Button danger size="small" onClick={onClickDelete(item)}>
            {t('delete')}
          </Button>
        </Space>
      )
    }
  ];

  const formKey = isEditMode && selectedItem?._id ? `edit-${selectedItem._id}` : 'create';

  return (
    <>
      <Modal
        title={modalTitle}
        open={isModal}
        footer={[]}
        onCancel={onCloseModal}
        maskClosable={false}
        destroyOnHidden
      >
        <Form
          key={formKey}
          layout="vertical"
          className="builder__form"
          name="builder_create_form"
          initialValues={formValues}
          onValuesChange={onChangeForm}
          onFinish={onFinishForm}
          disabled={is_loading_create || is_loading_update}
        >
          {renderModalBody()}
          <ModalFooter onCancel={onCloseModal} isLoading={is_loading_create} />
        </Form>
      </Modal>

      <div className="builder">
        <div className="builder__section builder__section__grid">
          {/* <Search placeholder={t('search')} value={metaData.search} onChange={onChangeSearch} /> */}
          <div></div>

          <Button type="primary" icon={<PlusOutlined />} iconPosition="end" onClick={onShowModal}>
            {create_title}
          </Button>
        </div>

        <div className="builder__section">
          <Table
            style={{ width: '100%' }}
            columns={updatedTableColumns}
            dataSource={table_data}
            scroll={{ x: 'max-content', y: 450 }}
            loading={is_loading_read || is_loading_delete}
            pagination={{
              total: table_meta?.total,
              pageSize: metaData?.perPage,
              onChange: onChangePagination,
              showTotal: (value) => (
                <Text strong>
                  {t('total')} {title.toLowerCase()}: {value}
                </Text>
              )
            }}
          />
        </div>
      </div>
    </>
  );
};
